/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { propsUtils, debounceInput } from '@ba-ui-toolkit/utils';
import TextInput from '@ba-ui-toolkit/textinput';
import SVGIcon from '@ba-ui-toolkit/svgicon';
import Tooltip from '@ba-ui-toolkit/tooltip';
import Button from '@ba-ui-toolkit/button';
import search16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/search_16';
import close16 from '@ba-ui-toolkit/ba-graphics/dist/icons-js/close_16';
import styles from './SearchInput.scss';
import stringResource from './nls/StringResource';

const searchInputClassName = styles.searchInputClassName.replace(/"/g, '');
const { sanitize, getInheritedProps } = propsUtils;

@debounceInput()
export default class SearchInput extends Component {
	static propTypes = {
		...TextInput.propTypes,
		expanded: PropTypes.bool,
		value: PropTypes.string,
		onClear: PropTypes.func,
		open: PropTypes.bool,
		hideLeftIcon: PropTypes.bool,
		className: PropTypes.string,
		style: PropTypes.object,
	};

	static defaultProps = {
		iconLeft: <SVGIcon bidi focusable={false} iconId={search16.id} />,
		className: '',
		style: {},
	};

	constructor(props) {
		super(props);
		this.state = {
			open: this.props.open,
		};
	}

	focus() {
		this._input && this._input.focus();
	}

	clear = () => {
		this.props.onChange && this.props.onChange('');
		this.props.onClear && this.props.onClear();
		this._input.focus();
	};

	close = () => {
		this.clear();
		this.setState({
			open: !this.state.open,
		});
	};

	_ref = input => {
		this._input = input;
	};

	_onClick = () => {
		this.setState({
			open: !this.state.open,
		});
	};

	render() {
		const { value, iconLeft, hideLeftIcon, className, style, expanded, ...others } = this.props;
		const leftIcon = !hideLeftIcon ? iconLeft : null;
		const textInputPassthroughProps = getInheritedProps(others, TextInput);
		const clearText = stringResource.get('clear');
		const cancelText = stringResource.get('cancel');

		let icon;

		if (value && value.length) {
			icon = (
				<Tooltip
					orient="top"
					title={clearText}
					className={`${searchInputClassName}__clear`}
				>
					<Button
						className={`${searchInputClassName}__clear-button`}
						data-tid="ba-common-search-input__clear-button"
						icon={close16.id}
						onClick={this.clear}
						iconSize="small"
						variant="icon"
						intent="primary"
						aria-label={clearText}
					/>
				</Tooltip>
			);
		}

		if (!expanded || this.state.open) {
			return (
				<div className={`${searchInputClassName} ${className}`} style={style}>
					<TextInput
						data-tid="ba-common-search-input__search-input"
						role="search"
						{...sanitize(others, SearchInput)}
						{...textInputPassthroughProps}
						value={value}
						ref={this._ref}
						iconLeft={leftIcon}
						fireOnBlur={false}
						noBottomBorder
						inputChildren={icon}
					/>
					{expanded ? (
						<div
							className={`${searchInputClassName}__cancel-button cancel`}
							onClick={this.close}
						>
							{cancelText}
						</div>
					) : (
						undefined
					)}
				</div>
			);
		}

		return (
			<div className={`${searchInputClassName}__collapsed`}>
				<div
					className={`${searchInputClassName}__collapsed_icon collapsed`}
					onClick={this._onClick}
				>
					{leftIcon}
				</div>
			</div>
		);
	}
}
